import { ADVISOR, Z_SHARECLASS } from '@literature/const/literature-data-const';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Product, ShareClass } from '@models';
import {
  IUserProfile,
  PersonalisationAPIService,
  ProfileService,
  SegmentService,
} from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  Audience,
  CustomLiteratureDocumentListingData,
  EmailDocsResponse,
  ExcelDocType,
  KeyIdentifiers,
  LiteratureDocument,
  LiteratureDocumentListingData,
  LiteratureDocumentListingState,
  NoYesFlag,
  SegmentId,
  ShareClassCode,
} from '@types';
import camelCase from 'lodash/camelCase';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export class LiteratureListingBaseService {
  documentListingData$ = new Subject<LiteratureDocumentListingState>();
  state: LiteratureDocumentListingState = { isError: false };
  cartDocIds: string[] = [];
  loggedInColumnToDisplay: string[] = [];
  clientUseYesCount = 0;
  isInternational: boolean;
  user: IUserProfile;
  unsubscribe$: Subject<void> = new Subject<void>();
  excelDataMap: Map<string, ExcelDocType[]> = new Map();
  excelFundLevelData: ExcelDocType[] = [];
  // to remove extra doc type from excel
  documentsAvailableForExcel: string[] = [];
  isLoggedIn: boolean;
  isIdentified: boolean;
  isPfgiToken: boolean;

  constructor(
    protected literatureDataService: LiteratureDataService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected cartService: CartHandlerService,
    protected segmentService: SegmentService,
    protected profileService: ProfileService,
    protected personalisationService: PersonalisationAPIService,
    protected translateService: TranslateService
  ) {
    this.isInternational = this.literatureConfigService.isInternational;
    this.profileService
      .isLoggedIn$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      });

    this.personalisationService
      .isIdentified$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isIdentified: boolean) => {
        this.isIdentified = isIdentified;
      });
    this.personalisationService
      .getFirmPfgiToken$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((firmPfgiToken: string) => {
        this.isPfgiToken = !!firmPfgiToken;
      });
  }

  getLiteratureDocumentListingData(
    litData: LiteratureDocument,
    productData: Product
  ): LiteratureDocumentListingData {
    return {
      literatureData: litData,
      product: productData,
    };
  }

  getCustomLiteratureDocumentListingData(
    litData: any,
    productData: Product,
    shareClassArray: string[],
    identifiers: KeyIdentifiers
  ): CustomLiteratureDocumentListingData {
    const shareClass = [];
    shareClassArray.forEach((el) => {
      if (el !== 'SINGLCLASS') {
        productData.shareClasses.forEach((data) => {
          if (data.shareClassCode === el) {
            shareClass.push(data.shareClassName);
          }
        });
      }
    });
    return {
      literatureData: litData,
      product: productData,
      shareClass: shareClass.length > 0 ? shareClass : ['-'],
      identifier: identifiers,
    };
  }
  register(): Observable<LiteratureDocumentListingState> {
    return this.documentListingData$.asObservable();
  }

  getDownloadUrl(type: string): string {
    let target;
    const isDirect: boolean = type
      ? type.indexOf('https') !== -1
        ? true
        : false
      : false;
    if (isDirect) {
      target = type;
    } else {
      if (!this.isInternational) {
        target = this.literatureDataService.getDownloadUrl(type);
      } else {
        target = this.literatureDataService.getDownloadUrl('listing'); // add literatureHref for intl
      }
    }
    return target;
  }

  checkShareClassAvailablity(
    litData: LiteratureDocument,
    ppssData: any,
    showShareClass = false
  ) {
    litData.allShareClass = [];
    if (litData.frkShareClassCode?.length > 0) {
      const shareClasses: ShareClass[] = ppssData.shareClasses;
      let found = false;
      let isPrimaryShareClass = false;
      for (const shareclass of shareClasses) {
        isPrimaryShareClass = false;
        const pdsShareclassCode: ShareClassCode =
          shareclass.identifiers.shareClassCode;
        found = false;
        litData.frkShareClassCode.forEach((litShareClassCode: string) => {
          if (
            pdsShareclassCode === Z_SHARECLASS &&
            litShareClassCode.toLowerCase() === ADVISOR
          ) {
            litShareClassCode = Z_SHARECLASS;
          }
          if (
            this.mapRomanAndNumericShareClass(litShareClassCode) ===
            this.mapRomanAndNumericShareClass(pdsShareclassCode)
          ) {
            litData.shareclass = shareclass;

            // WDE-5738:
            // if isEtfClass is true it means its a hybrid fund.
            if (shareclass && shareclass.isEtfClass) {
              // ETF do not have shareClassFeeStructure, so we need to display shareClassCode in the dropdown.
              shareclass.shareClassFeeStructure = shareclass.shareClassCode;
            }

            // to push all shareclasses associated with a particular document to the literature document object
            litData.allShareClass.push(shareclass);
            found = true;
          }
        });
        if (found) {
          isPrimaryShareClass = shareclass.primaryShareClass;
          // break;
        }
      }
      // to display documnet on fund id level when configured from BR on PPSS detail page
      if (!showShareClass && found) {
        // show share class name when document is tagged with only one share class
        // to hide share class name when document is tagged to primary share class
        if (litData.frkShareClassCode?.length === 1 && !isPrimaryShareClass) {
          litData.showShareClass = true;
        } else {
          litData.showShareClass = false;
        }
      } else {
        litData.showShareClass =
          litData.frkShareClassCode?.length === 1 ? true : false;
      }
    }
  }

  loadDocumentList(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    isUnassociated?: boolean
  ) {
    // WDE-7880: Unassociated option will work as “add on” meaning display both types (product & non product)
    if (
      this.isInternational &&
      (this.isShareClassValid(litData) || isUnassociated)
    ) {
      if (
        this.literatureConfigService.isCanada &&
        litData?.availableForLoggedInUser === NoYesFlag.YES
      ) {
        if (this.isLoggedIn) {
          this.addToList(litData, ppssData, listingData);
        }
      } else {
        this.addToList(litData, ppssData, listingData);
      }
    } else if (!this.isInternational) {
      if (this.isShareClassValid(litData)) {
        this.isDocForLoggedIn(litData);
        this.addToList(litData, ppssData, listingData);
      }
    }
  }

  addToList(
    litData: LiteratureDocument,
    ppssData: Product,
    listingData: LiteratureDocumentListingData[]
  ) {
    this.literatureDataService.mapLiteratureUrl(litData);
    const literatureDocumentListingData: LiteratureDocumentListingData = this.getLiteratureDocumentListingData(
      litData,
      ppssData
    );

    literatureDocumentListingData.literatureData.rowId = this.getUniqueID(
      literatureDocumentListingData
    );

    literatureDocumentListingData.literatureData.fundNameGenerated = this.getFundNameGenerated(
      literatureDocumentListingData
    );
    listingData.push(literatureDocumentListingData);
    // To prevent marketing materials in excel for US
    if (literatureDocumentListingData?.product?.fundId) {
      this.prepareExcelData(literatureDocumentListingData);
    }
  }

  // creating unique id with the combination of docId+fundId+ShareClassCode+fundUmbrella
  getUniqueID(
    literatureDocumentListingData: LiteratureDocumentListingData
  ): string {
    let uniqueId = literatureDocumentListingData?.literatureData?.rowId;
    if (literatureDocumentListingData?.product?.fundId) {
      uniqueId += '--' + literatureDocumentListingData?.product?.fundId;
    }
    if (literatureDocumentListingData?.literatureData?.shareclass) {
      uniqueId +=
        '--' +
        literatureDocumentListingData?.literatureData?.shareclass
          ?.shareClassCode;
    }

    if (literatureDocumentListingData?.product?.fundUmbrella) {
      uniqueId += '--' + literatureDocumentListingData?.product?.fundUmbrella;
    }
    return uniqueId;
  }

  public getFundNameGenerated(data: LiteratureDocumentListingData): string {
    const fundName = this.isInternational
      ? nameGenerator(
          data,
          this.translateService,
          this.literatureConfigService.isCanada
        )
      : fundNameGenerator(data, this.translateService);
    return fundName;
  }

  prepareExcelData(
    literatureDocumentListingData: LiteratureDocumentListingData
  ): void {
    let docLinks: ExcelDocType;
    let finalData: ExcelDocType[];
    let tempData: ExcelDocType[];
    const shareClassCodes =
      literatureDocumentListingData?.literatureData?.allShareClass;
    if (shareClassCodes && shareClassCodes.length > 0) {
      shareClassCodes.forEach((shareClass) => {
        const keyName =
          literatureDocumentListingData.product.fundName.toLowerCase() +
          '-' +
          shareClass?.shareClassName +
          '-' +
          literatureDocumentListingData.product.fundId;
        if (this.excelDataMap.has(keyName)) {
          tempData = this.excelDataMap.get(keyName);
          docLinks = this.setDocLink(
            literatureDocumentListingData.literatureData?.dctermsType,
            literatureDocumentListingData.literatureData?.pdfDownloadLink
          );
          finalData = cloneDeep(tempData);
          finalData.push(docLinks);
          this.excelDataMap.set(keyName, finalData);
        } else {
          const excelObject: ExcelDocType[] = this.createExcelObject(
            literatureDocumentListingData,
            shareClass
          );
          docLinks = this.setDocLink(
            literatureDocumentListingData.literatureData?.dctermsType,
            literatureDocumentListingData.literatureData?.pdfDownloadLink
          );
          finalData = cloneDeep(excelObject);
          finalData.push(docLinks);
          this.excelDataMap.set(keyName, finalData);
        }
      });
    } else {
      if (
        this.excelFundLevelData[literatureDocumentListingData.product.fundId]
      ) {
        this.excelFundLevelData[
          literatureDocumentListingData.product.fundId
        ].push(
          this.setDocLink(
            literatureDocumentListingData.literatureData?.dctermsType,
            literatureDocumentListingData.literatureData?.pdfDownloadLink
          )
        );
      } else {
        this.excelFundLevelData[
          literatureDocumentListingData.product.fundId
        ] = [
          this.setDocLink(
            literatureDocumentListingData.literatureData?.dctermsType,
            literatureDocumentListingData.literatureData?.pdfDownloadLink
          ),
        ];
      }
    }
  }

  createExcelObject(
    literatureDocumentListingData: LiteratureDocumentListingData,
    shareClass: ShareClass
  ): ExcelDocType[] {
    const temp: ExcelDocType[] = [];
    temp.push({
      columnHeader: 'fundId',
      columnValue: literatureDocumentListingData.product?.fundId,
    });
    temp.push({
      columnHeader: 'fundName',
      columnValue: literatureDocumentListingData.product?.fundName,
    });
    temp.push({
      columnHeader: 'shareClassName',
      columnValue: shareClass?.shareClassName,
    });
    temp.push({
      columnHeader: 'shareClassCurrency',
      columnValue: shareClass?.shareClassCurrency,
    });

    temp.push({
      columnHeader: 'ticker',
      columnValue: shareClass?.identifiers?.ticker,
    });
    temp.push({
      columnHeader: 'isin',
      columnValue: shareClass?.identifiers?.isin,
    });
    temp.push({
      columnHeader: 'sedol',
      columnValue: shareClass?.identifiers?.sedol,
    });
    temp.push({
      columnHeader: 'bloomberg',
      columnValue: shareClass?.identifiers?.bloomberg,
    });
    temp.push({
      columnHeader: 'taid',
      columnValue: shareClass?.identifiers?.taid,
    });
    temp.push({
      columnHeader: 'cusip',
      columnValue: shareClass?.identifiers?.cusip,
    });
    temp.push({
      columnHeader: 'reuterSystem',
      columnValue: shareClass?.identifiers?.reuterSystem,
    });
    temp.push({
      columnHeader: 'countryCode',
      columnValue: shareClass?.identifiers?.countryCode,
    });
    temp.push({
      columnHeader: 'languageCode',
      columnValue: shareClass?.identifiers?.languageCode,
    });
    temp.push({
      columnHeader: 'mexId',
      columnValue: shareClass?.identifiers?.mexId,
    });
    temp.push({
      columnHeader: 'apir',
      columnValue: shareClass?.identifiers?.apir,
    });
    temp.push({
      columnHeader: 'arsn',
      columnValue: shareClass?.identifiers?.arsn,
    });
    temp.push({
      columnHeader: 'anbimaCode',
      columnValue: shareClass?.identifiers?.anbimaCode,
    });
    temp.push({
      columnHeader: 'vln',
      columnValue: shareClass?.identifiers?.vln,
    });
    temp.push({
      columnHeader: 'intradayNav',
      columnValue: shareClass?.identifiers?.intradayNav,
    });
    temp.push({
      columnHeader: 'rfi',
      columnValue: shareClass?.identifiers?.rfi,
    });
    temp.push({
      columnHeader: 'adm',
      columnValue: shareClass?.identifiers?.adm,
    });
    temp.push({
      columnHeader: 'dsc',
      columnValue: shareClass?.identifiers?.dsc,
    });
    temp.push({
      columnHeader: 'iress',
      columnValue: shareClass?.identifiers?.iress,
    });
    temp.push({
      columnHeader: 'iressinavCode',
      columnValue: shareClass?.identifiers?.iressinavCode,
    });
    temp.push({
      columnHeader: 'fundbmTicker',
      columnValue: shareClass?.identifiers?.fundbmTicker,
    });
    temp.push({
      columnHeader: 'wkn',
      columnValue: shareClass?.identifiers?.wkn,
    });
    temp.push({
      columnHeader: 'nasdaqxnms',
      columnValue: shareClass?.identifiers?.nasdaqxnms,
    });
    temp.push({
      columnHeader: 'iopv',
      columnValue: shareClass?.identifiers?.iopv,
    });
    temp.push({
      columnHeader: 'frontend',
      columnValue: shareClass?.identifiers?.frontend,
    });
    temp.push({
      columnHeader: 'lowload',
      columnValue: shareClass?.identifiers?.lowload,
    });
    temp.push({
      columnHeader: 'mlidentifier',
      columnValue: shareClass?.identifiers?.mlidentifier,
    });
    return temp;
  }

  setDocLink(dctermType: string, link: string): ExcelDocType {
    if (!this.documentsAvailableForExcel.includes(camelCase(dctermType))) {
      this.documentsAvailableForExcel.push(camelCase(dctermType));
    }
    const domain = link?.includes('https') ? '' : this.extractDomain();
    return { columnHeader: camelCase(dctermType), columnValue: domain + link };
  }

  extractDomain(): string {
    const pathArray = this.literatureConfigService.getCmsBaseUrl()?.split('/');
    if (pathArray) {
      const protocol = pathArray[0];
      const host = pathArray[2];
      const url = protocol + '//' + host;
      return url;
    }
    return '';
  }

  isShareClassValid(litData: LiteratureDocument): boolean {
    if (litData.frkShareClassCode?.length === 0 || litData.shareclass) {
      return true;
    }
    return false;
  }
  loadCustomDocumentList(
    value: any,
    ppssData: Product,
    isAplicableShareClass: string[],
    identifiers: KeyIdentifiers,
    customListingData: CustomLiteratureDocumentListingData[],
    isShareclassLevelRegulatory = false
  ): void {
    // DCE-2162 : if isShareclassLevelRegulatory come as true from br than new regulatory ui will display
    if (isShareclassLevelRegulatory) {
      const customRow = this.getCustomLiteratureDocumentListingData(
        value,
        ppssData,
        isAplicableShareClass,
        identifiers
      );
      // DCE-1692 separating funds on multiple rows as per share class
      customRow.shareClass.forEach((shlCode) => {
        const dataRef = cloneDeep(customRow);
        dataRef.shareClass = [shlCode];
        customListingData.push(dataRef);
      });
    } else {
      customListingData.push(
        this.getCustomLiteratureDocumentListingData(
          value,
          ppssData,
          isAplicableShareClass,
          identifiers
        )
      );
    }
  }

  mapRomanAndNumericShareClass(shareClass: string): string {
    if (shareClass === 'I') {
      return '1';
    }
    if (shareClass === 'II') {
      return '2';
    }
    if (shareClass === 'Primary') {
      return 'P';
    }
    if (shareClass === 'Service') {
      return 'S';
    }
    return shareClass;
  }

  getClientUseCount(litData: LiteratureDocumentListingData[]): number {
    this.clientUseYesCount = 0;
    for (const data of litData) {
      if ('Yes' === data?.literatureData?.clientUse) {
        this.clientUseYesCount++;
      }
    }
    return this.clientUseYesCount;
  }

  postEmailDocs(
    recipients: string[],
    litCodes: string[],
    showStatus?: (statusMessage: string) => void
  ) {
    this.literatureDataService
      .emailDocuments(recipients, litCodes)
      .subscribe((response: EmailDocsResponse) => {
        showStatus(response.message);
      });
  }
  isDocForLoggedIn(litData: LiteratureDocument) {
    const userProfile = this.literatureConfigService.getUserProfile();
    if (litData.clientUse) {
      litData.loginRequired =
        (!litData.clientUse || litData.clientUse === 'No') &&
        !(userProfile.isLoggedIn || this.isIdentified || this.isPfgiToken);
    } else {
      litData.loginRequired = false;
    }
  }
  isInvestor(): boolean {
    return SegmentId.INVESTOR === this.segmentService.getCurrentSegmentId();
  }

  onDestroyUnsubscribe() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
